
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

export const StripeCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const finalizeConnection = async () => {
      
      try {
        // Get code and state from URL
        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const scope = searchParams.get("scope");

        if (!code || !state) {
          console.error("StripeCallback - Missing required parameters");
          throw new Error("Missing required parameters");
        }

        // First, ensure we have a valid session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError || !session) {
          console.error("StripeCallback - No valid session:", sessionError);
          localStorage.setItem('redirectAfterAuth', window.location.href);
          navigate("/login");
          return;
        }

        const { data, error: connectionError } = await supabase.functions.invoke(
          "finalize-stripe-connection",
          {
            body: { code, state },
          }
        );


        if (connectionError) {
          console.error("StripeCallback - Connection error:", connectionError);
          throw connectionError;
        }

        toast.success("Successfully connected Stripe account");
        navigate("/dashboard");
      } catch (err: any) {
        console.error("StripeCallback - Error finalizing connection:", err);
        setError(err.message || "Failed to connect Stripe account");
        toast.error(err.message || "Failed to connect Stripe account");
      } finally {
        setIsProcessing(false);
      }
    };

    finalizeConnection();
  }, [searchParams, navigate]);

  if (isProcessing) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-lg text-muted-foreground">
          Finalizing Stripe connection...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-lg text-red-500">{error}</p>
        <Button onClick={() => navigate("/dashboard")}>Return to Dashboard</Button>
      </div>
    );
  }

  return null;
};

export default StripeCallback;
